import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Heart, Users, Shield, Lock, HandHeart, Eye, Plus, X, Send, ChevronDown, ChevronUp, CheckCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import ThreadedComments from "@/components/ThreadedComments";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoLight } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const defaultStories = [
  { title: "From HR Executive to HR Entrepreneur \u2014 My RoleIQ Journey", author_name: "Priya Sharma", author_role: "CHRO", author_industry: "Manufacturing", category: "Career Growth" },
  { title: "How I Built a Competency Framework for 5000 Employees in 3 Weeks", author_name: "Rahul Mehta", author_role: "L&D Head", author_industry: "Banking", category: "HR Strategy" },
  { title: "Returning to Work After Maternity Leave \u2014 What Nobody Tells You", author_name: "Anita Verma", author_role: "HR Manager", author_industry: "IT", category: "She Leads" },
  { title: "Why I Left a Fortune 500 to Build Something That Matters", author_name: "Vikram Singh", author_role: "OD Consultant", author_industry: "Consulting", category: "Career Growth" },
  { title: "Building a High Performance Culture Without a Big Budget", author_name: "Deepa Krishnan", author_role: "HR Director", author_industry: "Retail", category: "Leadership" },
  { title: "How AI Changed the Way I Think About Talent Development", author_name: "Sanjay Nair", author_role: "Talent Head", author_industry: "FMCG", category: "AI in HR" },
];

const defaultDiscussions = [
  { title: "What is the best way to get leadership buy-in for a competency framework rollout?", author_name: "Meera Patel", author_role: "HR Manager", created_at: "2 hours ago", replies: 12, likes: 24, category: "HR Strategy" },
  { title: "Has anyone used AI tools for succession planning?", author_name: "Arjun Kapoor", author_role: "Talent Lead", created_at: "5 hours ago", replies: 8, likes: 15, category: "AI in HR" },
  { title: "How do you handle performance reviews when managers resist structured frameworks?", author_name: "Sunita Rao", author_role: "OD Specialist", created_at: "1 day ago", replies: 18, likes: 31, category: "Leadership" },
  { title: "Best practices for competency-based interviewing?", author_name: "Kiran Joshi", author_role: "TA Head", created_at: "1 day ago", replies: 14, likes: 22, category: "Competency & Skills" },
  { title: "How is AI changing the role of HR professionals in India?", author_name: "Nisha Gupta", author_role: "HRBP", created_at: "2 days ago", replies: 25, likes: 40, category: "AI in HR" },
];

const mentors = []; // Dynamic - loaded from API

const categories = ["All", "Career Growth", "HR Strategy", "Leadership", "Competency & Skills", "AI in HR", "Industry Insights", "Tools & Tech"];

function timeAgo(isoString) {
  if (!isoString || isoString.includes("ago")) return isoString;
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function CreatePostModal({ isOpen, onClose, onCreated, postType }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Career Growth");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) { setError("Title and content are required"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/api/posts`, { title, content, category, post_type: postType }, { withCredentials: true });
      setTitle(""); setContent(""); setCategory("Career Growth");
      onCreated();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create post. Are you logged in?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-playfair text-navy text-xl font-bold">
            {postType === "story" ? "Submit Your Story" : "Start a Discussion"}
          </h3>
          <button onClick={onClose} className="text-navy/30 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="post-title-input"
            className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
          <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Write your thoughts..." rows={5} data-testid="post-content-input"
            className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none resize-none" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="post-category-select"
            className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none">
            {categories.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {error && <p className="font-dm text-red-500 text-xs">{error}</p>}
          <button onClick={handleSubmit} disabled={loading} data-testid="create-post-submit"
            className="w-full bg-navy text-white font-dm font-bold py-3 rounded-lg hover:bg-gold hover:text-navy transition-colors disabled:opacity-50">
            {loading ? "Publishing..." : "Publish"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function DiscussionCard({ post, user, onLike }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // commentId to reply to
  const [replyText, setReplyText] = useState("");

  const d = post;

  const loadComments = async () => {
    if (!d._id) return;
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/api/posts/${d._id}/comments`);
      setComments(res.data);
    } catch (err) { console.error(err); }
    finally { setLoadingComments(false); }
  };

  const toggleExpand = () => {
    if (!expanded && d._id) loadComments();
    setExpanded(!expanded);
  };

  const submitComment = async (parentId = null, text = null) => {
    const content = (text ?? newComment).trim();
    if (!content || !d._id) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/api/posts/${d._id}/comments`, { content, parent_id: parentId }, { withCredentials: true });
      setComments(prev => [...prev, res.data]);
      if (parentId) { setReplyText(""); setReplyingTo(null); } else { setNewComment(""); }
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  };

  const likeComment = async (commentId) => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/api/comments/${commentId}/like`, {}, { withCredentials: true });
      setComments(prev => prev.map(c => c._id === commentId ? { ...c, likes: res.data.likes } : c));
    } catch (err) { console.error(err); }
  };

  // Build nested tree from flat comment list
  const childrenMap = {};
  const roots = [];
  comments.forEach(c => {
    const p = c.parent_id;
    if (p) {
      childrenMap[p] = childrenMap[p] || [];
      childrenMap[p].push(c);
    } else {
      roots.push(c);
    }
  });

  const renderComment = (c, depth = 0) => {
    const children = childrenMap[c._id] || [];
    const indent = Math.min(depth, 2) * 24; // cap visual indent at 2 levels
    return (
      <div key={c._id} className="" style={{ marginLeft: indent }} data-testid={`comment-${c._id}`}>
        <div className="flex gap-3 mb-2">
          <div className="w-7 h-7 bg-navy/10 rounded-full flex-shrink-0 flex items-center justify-center">
            <span className="font-dm text-navy/50 text-[10px] font-bold">{c.author_name?.[0]?.toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-dm text-navy text-xs font-semibold">{c.author_name}</span>
              {c.author_role && <span className="font-dm text-navy/40 text-[10px]">{c.author_role}</span>}
              <span className="font-dm text-navy/30 text-[10px]">{timeAgo(c.created_at)}</span>
            </div>
            <p className="font-dm text-navy/70 text-sm mt-0.5 break-words">{c.content}</p>
            <div className="flex items-center gap-3 mt-1">
              <button onClick={() => c._id && likeComment(c._id)} className="flex items-center gap-1 text-navy/40 hover:text-gold font-dm text-[10px] transition-colors">
                <Heart className="w-2.5 h-2.5" /> {c.likes || 0}
              </button>
              {user && depth < 2 && (
                <button onClick={() => { setReplyingTo(c._id === replyingTo ? null : c._id); setReplyText(""); }} data-testid={`reply-btn-${c._id}`}
                  className="text-navy/40 hover:text-gold font-dm text-[10px] font-semibold transition-colors">
                  {replyingTo === c._id ? "Cancel" : "Reply"}
                </button>
              )}
            </div>
            {replyingTo === c._id && (
              <div className="mt-2 flex gap-2" data-testid={`reply-form-${c._id}`}>
                <input value={replyText} onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Reply to ${c.author_name?.split(" ")[0] || "comment"}…`}
                  onKeyDown={(e) => e.key === "Enter" && submitComment(c._id, replyText)}
                  className="flex-1 border border-navy/15 rounded-lg px-2 py-1.5 font-dm text-xs focus:border-gold focus:outline-none" />
                <button onClick={() => submitComment(c._id, replyText)} disabled={posting || !replyText.trim()} data-testid={`submit-reply-${c._id}`}
                  className="bg-navy text-gold px-2.5 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50">Reply</button>
              </div>
            )}
          </div>
        </div>
        {children.length > 0 && (
          <div className="border-l-2 border-navy/5 pl-3">
            {children.map(child => renderComment(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <div className="p-5">
        <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full mb-2">{d.category}</span>
        <h3 className="font-dm text-navy font-semibold mb-1">{d.title}</h3>
        {d.content && <p className="font-dm text-navy/60 text-sm mb-2 line-clamp-2">{d.content}</p>}
        <div className="flex flex-wrap items-center gap-4 text-navy/50 font-dm text-xs">
          <span>{d.author_name || d.author} {d.author_role ? `\u00B7 ${d.author_role}` : (d.role ? `\u00B7 ${d.role}` : "")}</span>
          <span>{timeAgo(d.created_at) || d.time}</span>
          <button onClick={toggleExpand} className="flex items-center gap-1 hover:text-gold transition-colors" data-testid="toggle-comments">
            <MessageCircle className="w-3 h-3" /> {d.replies || 0}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <button onClick={() => d._id && onLike(d._id)} className="flex items-center gap-1 hover:text-gold transition-colors">
            <Heart className="w-3 h-3" /> {d.likes || 0}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-navy/5 px-5 py-4 bg-cream/30">
          {loadingComments ? (
            <p className="font-dm text-navy/40 text-xs">Loading replies...</p>
          ) : roots.length === 0 ? (
            <p className="font-dm text-navy/40 text-xs mb-3">No replies yet. Be the first!</p>
          ) : (
            <div className="space-y-1 mb-4">
              {roots.map(r => renderComment(r, 0))}
            </div>
          )}
          {user ? (
            <div className="flex gap-2">
              <input value={newComment} onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a reply..." data-testid="comment-input"
                onKeyDown={(e) => e.key === "Enter" && submitComment()}
                className="flex-1 border border-navy/15 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
              <button onClick={() => submitComment()} disabled={posting} data-testid="submit-comment"
                className="bg-navy text-gold px-3 py-2 rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50">
                <Send className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <p className="font-dm text-navy/40 text-xs"><a href="/login" className="text-gold hover:underline">Login</a> to reply</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function CommunityPage() {
  usePageTitle("Community");
  const [activeTab, setActiveTab] = useState("All");
  const [stories, setStories] = useState(defaultStories);
  const [discussions, setDiscussions] = useState(defaultDiscussions);
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [showDiscussionModal, setShowDiscussionModal] = useState(false);
  const [showMentorModal, setShowMentorModal] = useState(false);
  const [realMentors, setRealMentors] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const { user } = useAuth();

  const fetchPosts = async () => {
    try {
      const [storiesRes, discussionsRes] = await Promise.all([
        axios.get(`${API}/api/posts?post_type=story`),
        axios.get(`${API}/api/posts?post_type=discussion`),
      ]);
      setStories(storiesRes.data.length > 0 ? storiesRes.data : defaultStories);
      setDiscussions(discussionsRes.data.length > 0 ? discussionsRes.data : defaultDiscussions);
    } catch (err) { console.error(err); }
  };

  const fetchMentors = async () => {
    try {
      const res = await axios.get(`${API}/api/mentors`);
      setRealMentors(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchPosts(); fetchMentors(); }, []);

  const handleLike = async (postId) => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/api/posts/${postId}/like`, {}, { withCredentials: true });
      setDiscussions(prev => prev.map(d => d._id === postId ? { ...d, likes: res.data.likes } : d));
    } catch (err) { console.error(err); }
  };

  const filteredDiscussions = discussions.filter(d => activeTab === "All" || d.category === activeTab);

  return (
    <div>
      {/* Hero */}
      <section className="bg-white pt-20 pb-4" data-testid="community-branded-header">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between flex-wrap gap-3">
          <LogoLight width={180} height={42} />
          <span style={{ borderLeft: "3px solid #C9A84C", paddingLeft: "10px", fontFamily: "Georgia,serif", fontSize: "13px", color: "#0F1E35" }}>Community</span>
        </div>
        <div className="max-w-5xl mx-auto px-4 mt-4" style={{ height: "1px", background: "rgba(15,30,53,0.08)" }} />
      </section>
      <section className="bg-navy pt-12 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
            Real Stories. Real Conversations. Real Growth.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-dm text-white/70 text-lg max-w-xl mx-auto mb-8">
            RoleIQ Community is where HR professionals, leaders, and working professionals across all industries connect, share, and grow together.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            {user ? (
              <button onClick={() => setShowDiscussionModal(true)} className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg flex items-center gap-2 justify-center" data-testid="join-community-btn">
                <Plus className="w-4 h-4" /> Start a Discussion
              </button>
            ) : (
              <a href="/signup" className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg">Join Community</a>
            )}
            <a href="#discussions" className="border border-white text-white font-dm px-8 py-3 rounded-lg hover:bg-white/10 transition-colors">Browse Discussions</a>
          </motion.div>
          <p className="font-dm text-white/40 text-sm">Safe &middot; Moderated &middot; Professional &middot; Respectful &middot; Zero Spam</p>
        </div>
      </section>

      {/* Stories */}
      <section className="bg-cream py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-12">
            Hear from professionals who have been where you are
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {stories.map((s, i) => (
              <motion.div key={s._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                onClick={() => s._id && setSelectedStory(s)}
                data-testid={s._id ? `story-card-${s._id}` : `story-card-default-${i}`}
                className={`bg-white rounded-xl shadow-md p-6 hover:-translate-y-1 hover:shadow-lg transition-all ${s._id ? "cursor-pointer" : ""}`}
              >
                <span className="font-playfair text-gold text-3xl">&ldquo;</span>
                <h3 className="font-dm text-navy font-semibold mb-2 -mt-2">{s.title}</h3>
                <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full mb-4">{s.category}</span>
                {s.content && <p className="font-dm text-navy/60 text-sm mb-4 line-clamp-3">{s.content}</p>}
                <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-navy/5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-dm text-navy/50 text-[10px] font-bold">{(s.author_name || s.author || "?")?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-dm text-navy text-sm font-semibold truncate">{s.author_name || s.author}</p>
                      <p className="font-dm text-navy/50 text-xs truncate">{s.author_role || s.role}{s.author_industry || s.industry ? `, ${s.author_industry || s.industry}` : ""}</p>
                    </div>
                  </div>
                  {s._id && (
                    <span className="font-dm text-gold text-xs flex items-center gap-1 hover:underline whitespace-nowrap flex-shrink-0">
                      <MessageCircle className="w-3 h-3" /> {s.replies || 0}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center">
            {user ? (
              <button onClick={() => setShowStoryModal(true)} className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors" data-testid="submit-story-btn">Submit Your Story</button>
            ) : (
              <a href="/signup" className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors inline-block">Sign Up to Submit</a>
            )}
          </div>
        </div>
      </section>

      {/* Discussions */}
      <section className="bg-white py-20" id="discussions">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-8">
            Ask. Answer. Learn. Together.
          </motion.h2>
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map((c) => (
              <button key={c} onClick={() => setActiveTab(c)}
                className={`font-dm text-sm px-4 py-2 rounded-full transition-colors ${activeTab === c ? "bg-gold text-navy font-semibold" : "bg-navy/5 text-navy/60 hover:bg-navy/10"}`}
              >{c}</button>
            ))}
          </div>
          <div className="space-y-4 mb-8">
            {filteredDiscussions.map((d, i) => (
              <DiscussionCard key={d._id || i} post={d} user={user} onLike={handleLike} />
            ))}
          </div>
          <div className="text-center">
            {user ? (
              <button onClick={() => setShowDiscussionModal(true)} className="bg-navy text-gold font-dm font-bold px-8 py-3 rounded-lg hover:bg-navy/90 transition-colors" data-testid="start-discussion-btn">
                <span className="flex items-center gap-2 justify-center"><Send className="w-4 h-4" /> Start a Discussion</span>
              </button>
            ) : (
              <a href="/login" className="bg-navy text-gold font-dm font-bold px-8 py-3 rounded-lg hover:bg-navy/90 transition-colors inline-block">Login to Discuss</a>
            )}
            <p className="font-dm text-navy/40 text-xs text-center mt-4">All posts are reviewed before publishing to maintain quality and safety</p>
          </div>
        </div>
      </section>

      {/* Mentors */}
      <section className="bg-cream py-20">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-3">
            Find a Mentor. Be a Mentor.
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.1 }} className="font-dm text-navy/60 text-center mb-12">
            Connect with experienced professionals for guidance, feedback, and real conversations about your career
          </motion.p>
          {realMentors.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {realMentors.map((m, i) => (
                <motion.div key={m._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg p-6 text-center transition-shadow">
                  <div className="w-16 h-16 bg-navy/10 rounded-full border-2 border-gold mx-auto mb-3 flex items-center justify-center">
                    <span className="font-playfair text-gold text-xl font-bold">{m.full_name?.[0]}</span>
                  </div>
                  <h4 className="font-dm text-navy font-semibold">{m.full_name}</h4>
                  <p className="font-dm text-navy/50 text-xs mb-2">{m.role_company}</p>
                  <div className="flex flex-wrap justify-center gap-1 mb-4">
                    {(m.expertise || []).slice(0, 3).map(e => <span key={e} className="bg-navy/5 text-navy/60 font-dm text-[10px] px-2 py-0.5 rounded-full">{e}</span>)}
                  </div>
                  {m.linkedin_url && <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer" className="border border-navy text-navy font-dm text-sm px-4 py-1.5 rounded-lg hover:bg-navy hover:text-white transition-colors inline-block">Connect</a>}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-10 text-center mb-10">
              <p className="font-dm text-navy/60 text-sm mb-4">Be the first mentor in your industry. Register now and get 10 free tokens.</p>
            </div>
          )}
          <div className="text-center">
            {user ? (
              <button onClick={() => setShowMentorModal(true)} className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors" data-testid="register-mentor-btn">Register as Mentor</button>
            ) : (
              <a href="/signup" className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors inline-block">Sign Up to Become a Mentor</a>
            )}
          </div>
        </div>
      </section>

      {/* Guidelines */}
      <section className="bg-navy py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-white text-3xl font-semibold mb-10">Our Community Promise</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Be Respectful", desc: "Treat every member with dignity" },
              { icon: Eye, title: "No Spam", desc: "No self-promotion or selling" },
              { icon: HandHeart, title: "Support Each Other", desc: "Lift others as you climb" },
              { icon: Lock, title: "Protect Privacy", desc: "What's shared here stays here" },
            ].map((g, i) => (
              <div key={i} className="border border-white/10 rounded-xl p-5">
                <g.icon className="w-8 h-8 text-gold mx-auto mb-3" />
                <h4 className="font-dm text-white font-semibold text-sm mb-1">{g.title}</h4>
                <p className="font-dm text-white/50 text-xs">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modals */}
      <CreatePostModal isOpen={showStoryModal} onClose={() => setShowStoryModal(false)} onCreated={fetchPosts} postType="story" />
      <CreatePostModal isOpen={showDiscussionModal} onClose={() => setShowDiscussionModal(false)} onCreated={fetchPosts} postType="discussion" />
      <MentorModal isOpen={showMentorModal} onClose={() => setShowMentorModal(false)} onRegistered={fetchMentors} />
      {selectedStory && <StoryDetailModal story={selectedStory} onClose={() => setSelectedStory(null)} />}
    </div>
  );
}

function StoryDetailModal({ story, onClose }) {
  const { user } = useAuth();
  const [likes, setLikes] = useState(story.likes || 0);

  const likeStory = async () => {
    if (!user) { window.location.href = "/login"; return; }
    try {
      const r = await axios.post(`${API}/api/posts/${story._id}/like`, {}, { withCredentials: true });
      setLikes(r.data.likes);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="story-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white rounded-t-2xl z-10">
          <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full">{story.category}</span>
          <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="close-story-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 sm:p-8">
          <span className="font-playfair text-gold text-5xl block mb-2 leading-none">&ldquo;</span>
          <h2 className="font-playfair text-navy text-2xl sm:text-3xl font-bold mb-4 leading-tight">{story.title}</h2>
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-navy/10">
            <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center font-dm text-navy/60 text-sm font-bold">
              {(story.author_name || story.author || "?")?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-dm text-navy text-sm font-semibold">{story.author_name || story.author}</p>
              <p className="font-dm text-navy/50 text-xs">{story.author_role || story.role}{story.author_industry || story.industry ? ` · ${story.author_industry || story.industry}` : ""}</p>
            </div>
          </div>
          <p className="font-dm text-navy/80 text-[15px] leading-[1.85] whitespace-pre-wrap mb-6" data-testid="story-body">{story.content || "Story body coming soon."}</p>
          <div className="flex items-center gap-4 pt-4 border-t border-navy/10">
            <button onClick={likeStory} className="flex items-center gap-1.5 text-navy/60 hover:text-gold font-dm text-sm transition-colors" data-testid="story-like-btn">
              <Heart className="w-4 h-4" /> {likes}
            </button>
            <span className="font-dm text-navy/40 text-xs">Share your thoughts below</span>
          </div>

          {/* Threaded comments */}
          <ThreadedComments targetType="post" targetSlug={story._id} />
        </div>
      </div>
    </div>
  );
}

const expertiseOptions = ["Competency Design", "Talent Management", "Coaching", "Leadership Development", "HR Strategy", "Career Counselling", "Women in Leadership", "Succession Planning", "Performance Management"];

function MentorModal({ isOpen, onClose, onRegistered }) {
  const [form, setForm] = useState({ full_name: "", role_company: "", industry: "", years_experience: "", expertise: [], linkedin_url: "", bio: "", availability: "", gender: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  if (!isOpen) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleExpertise = (e) => {
    const arr = form.expertise.includes(e) ? form.expertise.filter(x => x !== e) : [...form.expertise, e];
    set("expertise", arr);
  };
  const submit = async () => {
    if (!form.full_name || !form.role_company || !form.industry) { setError("Please fill required fields"); return; }
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/mentors/register`, form, { withCredentials: true });
      setSuccess(true);
      onRegistered();
    } catch (err) { setError(err?.response?.data?.detail || "Registration failed. Are you logged in?"); }
    finally { setLoading(false); }
  };
  if (success) return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center" onClick={e => e.stopPropagation()}>
        <CheckCircle className="w-12 h-12 text-gold mx-auto mb-4" />
        <h3 className="font-playfair text-navy text-xl font-bold mb-2">Welcome to the RoleIQ Mentor Community!</h3>
        <p className="font-dm text-navy/60 text-sm mb-6">As a thank you, we have credited 10 tokens to your account. You can use these to generate competency maps on the platform.</p>
        <button onClick={onClose} className="bg-navy text-white font-dm font-bold px-8 py-3 rounded-lg">Done</button>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-playfair text-navy text-xl font-bold">Register as Mentor</h3>
          <button onClick={onClose} className="text-navy/30 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          <input required placeholder="Full Name *" value={form.full_name} onChange={e => set("full_name", e.target.value)} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" />
          <input required placeholder="Current Role & Company *" value={form.role_company} onChange={e => set("role_company", e.target.value)} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" />
          <select required value={form.industry} onChange={e => set("industry", e.target.value)} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none">
            <option value="">Industry *</option>
            {["Manufacturing","Automotive","Banking","IT/Technology","Retail","Healthcare","FMCG","Hospitality","Education","Logistics","Telecom","Real Estate","Consulting","Government","Pharmaceuticals","Other"].map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select value={form.years_experience} onChange={e => set("years_experience", e.target.value)} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none">
            <option value="">Years of Experience</option>
            {["1-3","3-5","5-10","10-15","15-20","20+"].map(y => <option key={y} value={y}>{y} years</option>)}
          </select>
          <div>
            <p className="font-dm text-navy text-sm font-semibold mb-2">Areas of Expertise</p>
            <div className="flex flex-wrap gap-2">
              {expertiseOptions.map(e => (
                <button key={e} type="button" onClick={() => toggleExpertise(e)}
                  className={`font-dm text-xs px-3 py-1.5 rounded-full transition-colors ${form.expertise.includes(e) ? "bg-gold text-navy font-semibold" : "bg-navy/5 text-navy/60"}`}>{e}</button>
              ))}
            </div>
          </div>
          <input placeholder="LinkedIn Profile URL" value={form.linkedin_url} onChange={e => set("linkedin_url", e.target.value)} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" />
          <textarea placeholder="Short Bio (max 200 words)" value={form.bio} onChange={e => set("bio", e.target.value)} rows={3} maxLength={1200} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none resize-none" />
          <select value={form.availability} onChange={e => set("availability", e.target.value)} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none">
            <option value="">Availability</option><option value="Weekends only">Weekends only</option><option value="Evenings only">Evenings only</option><option value="Flexible">Flexible</option>
          </select>
          <select value={form.gender} onChange={e => set("gender", e.target.value)} className="w-full border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none">
            <option value="">Gender</option><option value="Male">Male</option><option value="Female">Female</option><option value="Non-binary">Non-binary</option><option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
        {error && <p className="font-dm text-red-500 text-xs mt-2">{error}</p>}
        <button onClick={submit} disabled={loading} className="w-full bg-gold text-navy font-dm font-bold py-3 rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50 mt-4">
          {loading ? "Registering..." : "Register as Mentor"}
        </button>
      </div>
    </div>
  );
}
